import { Heart, ThumbsUp, Handshake } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface CommentReactionsProps {
  reactions: {
    likes: number;
    hearts: number;
    supports: number;
  };
  commentId: string;
  onAddReaction: (reactionType: 'likes' | 'hearts' | 'supports') => void;
}

export const CommentReactions = ({ reactions, commentId, onAddReaction }: CommentReactionsProps) => {
  const [localReactions, setLocalReactions] = useState(reactions);
  const [userReactions, setUserReactions] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const handleReaction = async (type: 'likes' | 'hearts' | 'supports') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to react to comments",
          variant: "destructive",
        });
        return;
      }

      if (userReactions.has(type)) {
        toast({
          description: "You've already reacted with this emoji",
        });
        return;
      }

      // Optimistic update
      setLocalReactions(prev => ({
        ...prev,
        [type]: (prev[type] || 0) + 1
      }));
      setUserReactions(prev => new Set([...prev, type]));

      // Update the comment's reactions in the database
      const { error } = await supabase
        .from('comments')
        .update({
          reactions: {
            ...localReactions,
            [type]: (localReactions[type] || 0) + 1
          }
        })
        .eq('id', commentId);

      if (error) throw error;

      onAddReaction(type);

      toast({
        description: "Thanks for spreading positivity! 🌟",
      });

    } catch (error) {
      console.error('Error adding reaction:', error);
      // Revert optimistic update
      setLocalReactions(reactions);
      setUserReactions(prev => {
        const newSet = new Set(prev);
        newSet.delete(type);
        return newSet;
      });
      
      toast({
        title: "Couldn't add reaction",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex items-center gap-4">
      <button 
        onClick={() => handleReaction('likes')}
        className={`flex items-center gap-1 text-sm transition-colors ${
          userReactions.has('likes') 
            ? 'text-primary' 
            : 'text-muted-foreground hover:text-primary'
        }`}
        disabled={userReactions.has('likes')}
      >
        <ThumbsUp className="w-4 h-4" />
        {localReactions.likes || 0}
      </button>
      <button 
        onClick={() => handleReaction('hearts')}
        className={`flex items-center gap-1 text-sm transition-colors ${
          userReactions.has('hearts') 
            ? 'text-red-500' 
            : 'text-muted-foreground hover:text-red-500'
        }`}
        disabled={userReactions.has('hearts')}
      >
        <Heart className="w-4 h-4" />
        {localReactions.hearts || 0}
      </button>
      <button 
        onClick={() => handleReaction('supports')}
        className={`flex items-center gap-1 text-sm transition-colors ${
          userReactions.has('supports') 
            ? 'text-green-500' 
            : 'text-muted-foreground hover:text-green-500'
        }`}
        disabled={userReactions.has('supports')}
      >
        <Handshake className="w-4 h-4" />
        {localReactions.supports || 0}
      </button>
    </div>
  );
};